/**
 * resetAllPlugin — Pinia plugin that adds a `resetStores(pinia, ids?)`
 * orchestrator. Resets cached state in lockstep with admin logout so
 * stale rows don't bleed into the next admin's session in the same tab.
 *
 * Why a plugin rather than per-store `$reset()`
 * ---------------------------------------------
 * `$reset()` works for options-syntax stores but not for setup-syntax
 * stores (which every store in this codebase uses): the setup function
 * has no declarative state shape Pinia can re-invoke. The idiomatic
 * substitute is to capture `JSON.parse(JSON.stringify(store.$state))`
 * the first time the plugin sees a store and replay it on reset.
 *
 * Scope
 * -----
 * `resetStores()` accepts an optional id-list OR an id-predicate so the
 * admin logout flow can reset only the admin-domain stores and leave the
 * user-domain stores untouched. A predicate is the discovery-time form:
 * the admin logout passes `id => id.startsWith("admin")` rather than a
 * hand-maintained literal, so a new admin slab is covered automatically.
 * Calling with no argument resets every active store.
 */

import type { Pinia, PiniaPluginContext } from "pinia";

/**
 * Per-store initial-state snapshots, keyed by the Pinia store id (not by
 * the store instance, because Pinia's instances are reactive Proxies and
 * keying by Proxy can subtly drop entries through trap interactions).
 *
 * Scoping by id is correct because within a Pinia instance an id resolves
 * to exactly one store. The id space is global to the registry, which is
 * what we want — the snapshot belongs to "the adminSessions slab", not to
 * "a particular Vue reactive proxy of it".
 */
const snapshots = new Map<string, Record<string, unknown>>();

/**
 * The plugin function. Register it on the Pinia instance:
 *
 *   const pinia = createPinia();
 *   pinia.use(resetAllPlugin);
 *   app.use(pinia);
 *
 * Then the orchestrator surface:
 *
 *   import { resetStores } from "@src/stores/plugins/resetAll.ts";
 *   resetStores(pinia, ["adminSessions", "adminResults", "adminDataSource"]);
 *   resetStores(pinia, (id) => id.startsWith("admin")); // discovery form
 */
export function resetAllPlugin({ store }: PiniaPluginContext): void {
    // Capture the initial state for this store id. The `replace`-semantics
    // here are intentional: each fresh Pinia instance (test isolation,
    // future "switch user" surfaces) re-installs the plugin and re-seeds
    // a clean snapshot. Within one Pinia instance the plugin runs once
    // per store id at activation time, so this is the right capture moment.
    snapshots.set(store.$id, JSON.parse(JSON.stringify(store.$state)));
}

/**
 * Pinia exposes the active-store map via the (lightly-undocumented but
 * stable since 2.x) `_s` property — a `Map<string, Store>` keyed by the
 * `defineStore` id. The public `Pinia` type doesn't surface it, so we
 * narrow once at the boundary through a structural type that mirrors the
 * runtime shape. This is a single boundary cast, not a `as unknown as`
 * laundering — the `_s` property is real and documented in Pinia's
 * source; we're just bridging the type wall.
 */
type StoreInstance = { $state: Record<string, unknown> };
type PiniaWithStores = Pinia & { _s: Map<string, StoreInstance> };

/**
 * Scope filter for `resetStores`: an explicit id-list, or a predicate
 * evaluated against each active store id at discovery time.
 */
export type ResetScope = readonly string[] | ((id: string) => boolean);

/**
 * Reset every active store registered against the given Pinia instance
 * (or only the ones the `scope` selects — an id-list or an id-predicate).
 *
 * We walk Pinia's active-store map, restore each store's snapshot
 * through `$state = snapshot`, and Pinia takes care of the reactive
 * rewire. Stores that were never activated (lazy slabs nobody touched)
 * are not in `_s` and don't need resetting — they'll capture their
 * initial snapshot the first time something calls them.
 */
export function resetStores(pinia: Pinia, scope?: ResetScope): void {
    const selects =
        scope === undefined
            ? () => true
            : typeof scope === "function"
              ? scope
              : ((set) => (id: string) => set.has(id))(new Set(scope));
    const stores = (pinia as PiniaWithStores)._s;
    for (const [id, store] of stores) {
        if (!selects(id)) continue;
        const snapshot = snapshots.get(id);
        if (!snapshot) continue;
        // Deep clone so a future mutation doesn't aliasing-bleed into the
        // stored snapshot.
        store.$state = JSON.parse(JSON.stringify(snapshot));
    }
}
